import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { useDownloadInvoice } from '@app/hooks';
import { selectBillingInfo } from '@app/redux/billingInfo';
import { Route, setBillingNav } from '@app/redux/billingNav';
import { selectConfig } from '@app/redux/config';
import { sendMessageToMobileApp } from '@app/redux/crossPlatformCommunication';

import { getInvoicePDFUrl } from '../get-invoice-pdf-url';
import LinkLoading from './link-loading';
import type { Invoice } from '@mfe/shared/schema-types';

import styles from './styles.module.scss';

const InvoiceLink = ({ invoice, index }: Pick<Props, 'invoice' | 'index'>) => {
  const { t } = useTranslation('BillingStatement');
  const [date, setDate] = React.useState<string | null>(null);
  const dispatch = useDispatch();
  const { isPWA } = useSelector(selectConfig);

  const [timestamp, setTimestamp] = React.useState<Date>();

  // PWA invoice loads different from non PWA invoice
  // On web none of this code is used
  const { base64Content: invoiceContent, loading: pwaInvoiceLoading } = useDownloadInvoice(date);
  const { loadingInfo: billingLoadingInfo } = useSelector(selectBillingInfo);
  const isCurrentNonPWAInvoiceLoading = Boolean(
    invoice?.invoiceNumber && billingLoadingInfo.invoicePdf.includes(invoice?.invoiceNumber)
  );
  const loading = pwaInvoiceLoading || isCurrentNonPWAInvoiceLoading;

  React.useEffect(() => {
    // Once downloading invoice has finished on the PWA
    // Save and open the file on the mobile app
    if (!invoiceContent || !date || !isPWA) return;
    dispatch(
      sendMessageToMobileApp({
        type: 'save-and-open-file',
        data: {
          file: {
            name: `viasat-invoice-${date}.pdf`,
            displayName: `Viasat Invoice - ${date}`,
            base64Content: invoiceContent,
          },
          onErrorDisplayMessage: {
            title: t('invoiceLoadFailed'),
            message: t('invoiceLoadFailedMessage'),
          },
        },
      })
    );
  }, [invoiceContent, date, dispatch, isPWA, t]);

  React.useEffect((): void => {
    // Once we have finished loading...
    if (!loading && timestamp) {
      setTimestamp(undefined);
    }
  }, [dispatch, index, loading, timestamp]);

  const onViewStatementClick = () => {
    setTimestamp(new Date());

    if (isPWA) {
      // Setting the date to non null value triggers useDownloadInvoice
      setDate(getInvoicePDFUrl(invoice.statementDate));
    } else {
      // Open the invoice in a new tab on the web
      window.open(`${window.location.origin}/Invoice/${getInvoicePDFUrl(invoice.statementDate)}`, '_blank');
    }
  };

  if (loading) {
    return <LinkLoading />;
  }

  return (
    <button className={styles['link']}>
      <Txt variant="bodySmallRegular" onClick={onViewStatementClick}>
        {t('viewStatement')}
      </Txt>
    </button>
  );
};

type Props = {
  invoice: Invoice;
  index: number;
  showPopupMessage: boolean;
};

const Links = ({ invoice, index, showPopupMessage }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation('BillingStatement');

  return (
    <>
      <div className={styles['links']}>
        <button
          className={styles['link']}
          data-cy="viewPaymentsBtn"
          onClick={() => dispatch(setBillingNav({ route: Route.Payments }))}
        >
          <Txt variant="bodySmallRegular">{t('viewPayments')}</Txt>
        </button>
        <InvoiceLink invoice={invoice} index={index} />
      </div>
      {showPopupMessage && (
        <div style={{ marginTop: '10px' }}>
          <Txt variant="bodySmallRegular">{t('popupBlockerMessage')}</Txt>
        </div>
      )}
    </>
  );
};

export default Links;
